import React, { useMemo, useState, useEffect, useRef } from 'react';
import './TakeCareAgencyDashboard.css';
import dashboardApi from '../components/dashboardApi';
import useTheme from '../hooks/useTheme';
import { EMOTION_KEYS } from '../utils/emotionConstants';
import RecordingControls from '../components/RecordingControls';
import { authFetch, authHeaders } from '../utils/authFetch';
const DEFAULT_AVATAR = '/profile.jpg';

function ProgressBar({ label, value, color }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div className="tc-prog">
      <div className="tc-prog-label-row">
        <span>{label}</span>
        <span className="tc-prog-pct">{pct}%</span>
      </div>
      <div className="tc-prog-track">
        <div className="tc-prog-fill" style={{ width: pct + '%', background: color }} />
      </div>
    </div>
  );
}

function TinyLine({ points, color }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (!Array.isArray(points) || points.length === 0) return;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const pad = 6;
    ctx.beginPath();
    ctx.strokeStyle = color || '#4e73df';
    ctx.lineWidth = 2;
    points.forEach((p, i) => {
      const x = pad + (i * (w - pad * 2)) / Math.max(1, points.length - 1);
      const norm = max === min ? 0.5 : (p - min) / (max - min);
      const y = h - pad - norm * (h - pad * 2);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }, [points, color]);
  return <canvas className="tc-tiny-line" ref={canvasRef} width={180} height={52} />;
}

export default function TakeCareAgencyDashboard() {
  const PREDICT_ENDPOINT = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_PREDICT_URL)
    ? process.env.REACT_APP_PREDICT_URL
    : ((typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
        ? 'http://localhost:5000/predict'
        : 'https://app.neurolonic.com/app/predict');
  const { theme, toggleTheme } = useTheme();
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [view, setView] = useState('patients');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [timer, setTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [patientForm, setPatientForm] = useState({ name: '', age: '', gender: 'Male', contact: '', type: 'Inpatient', staff: '' });
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [micClicks, setMicClicks] = useState(0);
  const [segmentRecording, setSegmentRecording] = useState(false);
  const [_segmentResults, setSegmentResults] = useState([]);
  const [sessionComplete, setSessionComplete] = useState(false);
  const recordingTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  // Note transcription recording refs
  const noteMediaRecorderRef = useRef(null);
  const noteAudioChunksRef = useRef([]);
  const noteStreamRef = useRef(null);
  const noteIntervalRef = useRef(null);
  const noteRecStateRef = useRef('idle');
  const [lastDataSource, setLastDataSource] = useState(null);
  const [_lastEndpoint, setLastEndpoint] = useState('');
  const [_lastLatencyMs, setLastLatencyMs] = useState(null);
  const [hasSpeech, setHasSpeech] = useState(true);
  const [speechWarning, setSpeechWarning] = useState('');
  const activeSessionPatientIdRef = useRef(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [liveNoteText, setLiveNoteText] = useState('');
  const [noteRecState, setNoteRecState] = useState('idle'); // idle | recording | transcribing | error
  const [noteError, setNoteError] = useState('');
  const [notesByPatient, setNotesByPatient] = useState({});
  const [sessionHistory, setSessionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [deletedNotes, setDeletedNotes] = useState(new Set());
  const [staffList, setStaffList] = useState([]);
  const [_staffLoading, setStaffLoading] = useState(true);
  const [staffQuery, setStaffQuery] = useState('');
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({ name: '', role: '', contact: '' });

  async function applyLatestSessionForPatient(patientId) {
    if (!patientId) return;
    try {
      const resp = await dashboardApi.getPatientSessions(patientId);
      if (resp && resp.status_code === 200 && Array.isArray(resp.data) && resp.data.length > 0) {
        const latest = resp.data[0];
        const emotions = latest.emotion_details || null;
        const update = {
          last_session: (latest.session_date || '').slice(0,10),
        };
        if (emotions) {
          update.eve = {
            calm: (emotions.calm || 0) / 100,
            happy: (emotions.happy || 0) / 100,
            stressed: (emotions.stressed || 0) / 100,
            demotivated: (emotions.demotivated || 0) / 100,
            anxious: (emotions.anxious || 0) / 100,
            hurt: (emotions.hurt || 0) / 100,
          };
          update.burnout_score = Math.round(emotions?.burnout_score || 0);
        }
        const dur = latest.duration;
        if (dur) {
          if (typeof dur === 'string' && dur.includes('seconds')) {
            const n = parseInt(dur, 10);
            if (!Number.isNaN(n)) update.last_session_duration = n;
          } else if (typeof dur === 'string' && dur.includes(':')) {
            const parts = dur.split(':').map(x => parseInt(x, 10));
            if (parts.length === 3 && parts.every(v => !Number.isNaN(v))) update.last_session_duration = parts[0]*3600 + parts[1]*60 + parts[2];
          }
        }
        setPatients(prev => prev.map(p => {
          if (p.id !== patientId) return p;
          if (!emotions) {
            const { eve, burnout_score, ...rest } = update;
            return { ...p, ...rest };
          }
          return { ...p, ...update };
        }));
      }
    } catch (e) {
      console.error('Failed to apply latest session for patient:', patientId, e);
    }
  }

  async function loadAllNotesForPatient(patientId) {
    if (!patientId) return;
    try {
      const sessionsResp = await dashboardApi.getPatientSessions(patientId);
      if (!sessionsResp || sessionsResp.status_code !== 200 || !Array.isArray(sessionsResp.data)) return;
      const sessions = sessionsResp.data;
      const notePromises = sessions
        .filter(s => !!s.session_id)
        .map(s => dashboardApi.getSessionNotes(s.session_id).then(r => ({ session: s, resp: r })).catch(() => ({ session: s, resp: null })));
      const results = await Promise.all(notePromises);
      const entries = [];
      for (const { session, resp } of results) {
        if (resp && resp.status_code === 200 && Array.isArray(resp.data)) {
          for (const n of resp.data) {
            const dateStr = (n.created_at || session.session_date || '').slice(0, 10);
            entries.push({ date: dateStr, text: n.note_text });
          }
        }
      }
      entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setNotesByPatient(prev => ({ ...prev, [patientId]: entries.map(e => `${e.date}: ${e.text}`) }));
    } catch (err) {
      console.error('Failed to load notes for patient:', patientId, err);
    }
  }

  async function loadSessionHistory(patientId) {
    if (!patientId) return;
    try {
      const resp = await dashboardApi.getPatientSessions(patientId);
      if (resp && resp.status_code === 200 && Array.isArray(resp.data)) {
        setSessionHistory(resp.data.map(s => ({
          id: s.session_id,
          date: (s.session_date || '').slice(0, 10),
          duration: s.duration,
          emotions: s.emotion_details || null
        })));
      }
    } catch (e) {
      console.error('Failed to load session history:', e);
    }
  }

  async function fetchWithRetry(fn, attempts = 2, delayMs = 700) {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try { return await fn(); } catch (e) { lastErr = e; await new Promise(r => setTimeout(r, delayMs)); }
    }
    throw lastErr;
  }

  useEffect(() => {
    let isMounted = true;
    async function loadAll() {
      setStaffLoading(true);
      setPatientsLoading(true);
      try {
        const [staffRes, patientsRes] = await Promise.all([
          fetchWithRetry(() => dashboardApi.getAllStaff()),
          fetchWithRetry(() => dashboardApi.getAllPatients()),
        ]);
        if (isMounted) {
          const staffData = (staffRes?.status_code === 200 ? staffRes.data : []).map(s => ({
            id: s.staff_id,
            name: s.name,
            role: s.designation,
            contact: s.contact,
            assignedPatients: []
          }));
          setStaffList(staffData);

          const basePatients = (patientsRes?.status_code === 200 ? patientsRes.data : []).map(p => ({
            id: p.patient_id,
            name: p.name,
            avatar: DEFAULT_AVATAR,
            status: p.type,
            last_session: new Date().toISOString().slice(0,10),
            age: p.age?.toString() || '',
            gender: p.gender || '',
            contact: p.contact || '',
            assigned_staff: p.assigned_staff_name || '',
            eve: { calm: 0, stressed: 0, demotivated: 0, anxious: 0, hurt: 0 },
            top_subcategories: [],
            burnout_score: 0,
            mental_state_delta: 0,
            missed_sessions: 0,
            language_fatigue: 0,
            prediction: { weekly: [], monthly: [] }
          }));

          // Hydrate each patient with their latest session so values persist on refresh
          const enrichedPatients = await Promise.all(basePatients.map(async (p) => {
            try {
              const resp = await dashboardApi.getPatientSessions(p.id);
              if (resp && resp.status_code === 200 && Array.isArray(resp.data) && resp.data.length > 0) {
                const latest = resp.data[0];
                const emotions = latest.emotion_details || null;
                const update = { ...p };
                if (latest.session_date) update.last_session = (latest.session_date || '').slice(0,10);
                if (emotions) {
                  update.eve = {
                    calm: (emotions.calm || 0) / 100,
                    happy: (emotions.happy || 0) / 100,
                    stressed: (emotions.stressed || 0) / 100,
                    demotivated: (emotions.demotivated || 0) / 100,
                    anxious: (emotions.anxious || 0) / 100,
                    hurt: (emotions.hurt || 0) / 100,
                  };
                  update.burnout_score = Math.round(emotions?.burnout_score || 0);
                }
                const dur = latest.duration;
                if (dur) {
                  if (typeof dur === 'string' && dur.includes('seconds')) {
                    const n = parseInt(dur, 10);
                    if (!Number.isNaN(n)) update.last_session_duration = n;
                  } else if (typeof dur === 'string' && dur.includes(':')) {
                    const parts = dur.split(':').map(x => parseInt(x, 10));
                    if (parts.length === 3 && parts.every(v => !Number.isNaN(v))) update.last_session_duration = parts[0]*3600 + parts[1]*60 + parts[2];
                  }
                }
                return update;
              }
            } catch {}
            return p;
          }));

          setPatients(enrichedPatients);
          // Recompute each staff's assignedPatients list from latest patients snapshot
          setStaffList(prev => prev.map(st => {
            const assigned = enrichedPatients
              .filter(p => (p.assigned_staff || '').trim() === (st.name || '').trim())
              .map(p => `${p.name}`);
            return { ...st, assignedPatients: assigned.length ? assigned : [] };
          }));
          if (enrichedPatients.length > 0 && !selectedPatientId) {
            setSelectedPatientId(enrichedPatients[0].id);
          }
        }
      } catch (e) {
        console.error('Failed to load dashboard data:', e);
      } finally {
        if (isMounted) {
          setStaffLoading(false);
          setPatientsLoading(false);
        }
      }
    }
    loadAll();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!patientsLoading && selectedPatientId) {
      applyLatestSessionForPatient(selectedPatientId);
      loadAllNotesForPatient(selectedPatientId);
      loadSessionHistory(selectedPatientId);
    }
  }, [patientsLoading, selectedPatientId]);

  useEffect(() => {
    if (selectedPatientId) {
      applyLatestSessionForPatient(selectedPatientId);
      loadAllNotesForPatient(selectedPatientId);
      loadSessionHistory(selectedPatientId);
    }
  }, [selectedPatientId]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTimer(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (deletedNotes.size > 0 && selectedPatientId) {
      setNotesByPatient(prev => {
        const notes = prev[selectedPatientId] || [];
        const filtered = notes.filter(n => !deletedNotes.has(n));
        if (filtered.length === notes.length) return prev;
        return { ...prev, [selectedPatientId]: filtered };
      });
    }
  }, [deletedNotes, selectedPatientId]);

  const selectedPatient = useMemo(() => patients.find(p => p.id === selectedPatientId), [patients, selectedPatientId]);

  const filtered = useMemo(() => {
    return patients.filter(p => {
      if (filter !== 'all' && p.status.toLowerCase() !== filter) return false;
      if (query && !p.name.toLowerCase().includes(query.toLowerCase()) && !p.id.includes(query)) return false;
      return true;
    });
  }, [patients, filter, query]);

  function hhmmss(total) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
  }

  // Map predict API response (Flask) to our dashboard metrics
  function mapApiResponseToMetrics(apiResult) {
    const res = (apiResult && (apiResult.RES || apiResult.res || apiResult.data || apiResult)) || {};
    const top5String = res.top_5 || '';
    const scores = {};
    EMOTION_KEYS.forEach(em => {
      try {
        // Match integers or decimals like 30% or 30.25%
        const re = new RegExp(`${em}\\s+(\\d+(?:\\.\\d+)?)%`, 'i');
        const m = top5String.match(re);
        scores[em] = m ? parseFloat(m[1]) / 100 : 0;
      } catch { scores[em] = 0; }
    });
    const total = Object.values(scores).reduce((s, v) => s + v, 0);
    if (total > 0) {
      Object.keys(scores).forEach(k => { scores[k] = scores[k] / total; });
    }
    const calm = scores.happy ?? 0;
    const happy = scores.happy ?? 0;
    const stressed = scores.angry ?? 0;
    const demotivated = scores.sad ?? 0;
    const anxious = scores.fear ?? 0;
    const hurt = scores.disgust ?? 0;
    const burnout = Math.round(((scores.angry || 0) + (scores.sad || 0) + (scores.fear || 0)) * 100) || 0;
    return { calm, happy, stressed, demotivated, anxious, hurt, burnout };
    }

  async function callPredictAPI(audioBlob) {
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.wav');
      const startTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      const response = await fetch(PREDICT_ENDPOINT, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`API call failed: ${response.status}`);
      const result = await response.json();
      setLastDataSource('api');
      setLastEndpoint(PREDICT_ENDPOINT);
      const endTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      setLastLatencyMs(Math.round(endTs - startTs));
      // Map actual API response into our metrics
      const mapped = mapApiResponseToMetrics(result);
      if (mapped && typeof mapped === 'object') return mapped;
      return null;
    } catch (error) {
      console.error('Predict API error:', error);
      setLastDataSource('error');
      setLastEndpoint(PREDICT_ENDPOINT);
      setLastLatencyMs(null);
      return null;
    }
  }

  function averageSegments(results) {
    if (results.length === 0) return null;
    const keys = ['calm','happy','stressed','demotivated','anxious','hurt'];
    const avg = keys.reduce((acc,k)=>({ ...acc, [k]: results.reduce((s,r)=>s+(r[k]||0),0)/results.length }),{});
    const burn = Math.round(results.reduce((s,r)=>s+(r.burnout||0),0)/results.length);
    return { eve: avg, burnout: burn };
  }

  async function saveSessionToDatabase(patientId, avgResults, sessionDuration) {
    try {
      const patient = patients.find(p => p.id === patientId);
      const assignedStaff = staffList.find(s => s.name === patient?.assigned_staff);
      if (!assignedStaff || !assignedStaff.id) return;
      const sessionResponse = await dashboardApi.createSession({
        patient_id: String(patientId),
        staff_id: String(assignedStaff.id),
        session_date: new Date().toISOString().split('T')[0],
        duration: `${sessionDuration} seconds`
      });
      if (sessionResponse.status_code === 200) {
        const sessionId = sessionResponse.data.session_id;
        setCurrentSessionId(sessionId);
        await dashboardApi.createEmotionDetails({
          patient_id: String(patientId),
          session_id: String(sessionId),
          calm: avgResults.eve.calm * 100,
          stressed: avgResults.eve.stressed * 100,
          anxious: avgResults.eve.anxious * 100,
          demotivated: avgResults.eve.demotivated * 100,
          hurt: avgResults.eve.hurt * 100,
          burnout_score: avgResults.burnout
        });
      }
    } catch (error) {
      console.error('Failed to save session to database:', error);
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const result = await callPredictAPI(audioBlob);
        if (!result) {
          setHasSpeech(false);
          setSpeechWarning('No speech detected or API error. Please try again.');
          return;
        }
        const emotionKeys = ['calm','happy','stressed','demotivated','anxious','hurt'];
        const allZero = emotionKeys.every(k => !result[k] || result[k] === 0);
        if (allZero) {
          setHasSpeech(false);
          setSpeechWarning('No speech detected in recording. Please ensure you are speaking near the microphone.');
          return;
        }
        setHasSpeech(true);
        setSpeechWarning('');
        setSegmentResults(prev => {
          const next = [...prev, result];
          if (next.length === 5) {
            const avg = averageSegments(next);
            if (avg) {
              const targetId = activeSessionPatientIdRef.current || selectedPatient.id;
              saveSessionToDatabase(targetId, avg, timer);
              setPatients(prevP => prevP.map(p => p.id !== targetId ? p : {
                ...p,
                last_session: new Date().toISOString().slice(0,10),
                last_session_duration: timer,
                eve: { ...p.eve, ...avg.eve },
                burnout_score: avg.burnout
              }));
            }
            setRunning(false);
          }
          return next;
        });
      };
      mediaRecorder.start();
      setSegmentRecording(true);
      setTimeout(() => { if (mediaRecorder.state === 'recording') { mediaRecorder.stop(); setSegmentRecording(false); } }, 8000);
    } catch (error) {
      console.error('Recording failed:', error);
      alert('Microphone access denied or recording failed. Please check permissions.');
      setSegmentRecording(false);
    }
  }

  function handleMicClick() {
    if (!selectedPatient) return;
    if (micClicks >= 5 || segmentRecording) return;
    if (sessionComplete) {
      setSessionComplete(false);
      setMicClicks(0);
      setSegmentResults([]);
      setTimer(0);
      setRunning(false);
      setSpeechWarning('');
    }
    if (micClicks === 0) { setTimer(0); setRunning(true); activeSessionPatientIdRef.current = selectedPatient.id; }
    setMicClicks(c => c + 1);
    startRecording();
  }

  useEffect(() => {
    try { 
      if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
      // Stop note recording if switching patients
      if (noteIntervalRef.current) { clearInterval(noteIntervalRef.current); noteIntervalRef.current = null; }
      if (noteMediaRecorderRef.current && noteMediaRecorderRef.current.state === 'recording') noteMediaRecorderRef.current.stop();
      if (noteStreamRef.current) noteStreamRef.current.getTracks().forEach(track => track.stop());
    } catch {}
    setMicClicks(0);
    setSegmentResults([]);
    setSegmentRecording(false);
    setRunning(false);
    setTimer(0);
    setSessionComplete(false);
    setCurrentSessionId(null);
    activeSessionPatientIdRef.current = null;
    setNoteRecState('idle');
    setNoteError('');
  }, [selectedPatientId]);

  useEffect(() => {
    if (micClicks === 5 && !segmentRecording && !running) {
      setSessionComplete(true);
      const targetId = activeSessionPatientIdRef.current;
      if (!targetId) return;
      const endedAt = new Date().toLocaleString();
      setPatients(prev => prev.map(p => p.id !== targetId ? p : { ...p, last_session: endedAt, last_session_duration: timer }));
      activeSessionPatientIdRef.current = null;
    }
  }, [micClicks, segmentRecording, running, timer, setPatients]);

  const showEscalateButton = selectedPatient && selectedPatient.burnout_score > 70;

  async function transcribeNoteAudio(audioBlob) {
    try {
      setNoteRecState('transcribing');
      setNoteError('');
      // Prefer server-side transcription in production, fallback to client key only if present
      const useServer = true;
      let text = '';
      if (useServer) {
        const fd = new FormData();
        fd.append('file', audioBlob, 'note-recording.webm');
        fd.append('language', 'en');
        const base = (typeof window !== 'undefined' && window.location && window.location.hostname && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
          ? 'http://localhost:8000'
          : 'https://app.neurolonic.com/api';
        const r = await authFetch(`${base}/transcribe`, { method: 'POST', headers: authHeaders(), body: fd });
        if (!r.ok) {
          const t = await r.text().catch(() => '');
          throw new Error(`Server transcription failed (${r.status}): ${t || r.statusText}`);
        }
        const j = await r.json();
        if (j && j.status_code === 200 && j.data && typeof j.data.text === 'string') {
          text = j.data.text;
        } else {
          text = '';
        }
      } else {
        // Client-side fallback (not used by default)
        const fromProcess = (typeof process !== 'undefined' && process.env)
          ? (process.env.OPENAI_API_KEY || process.env.REACT_APP_OPENAI_API_KEY)
          : undefined;
        const fromWindow = (typeof window !== 'undefined' && window.OPENAI_API_KEY) ? window.OPENAI_API_KEY : undefined;
        const apiKey = fromProcess || fromWindow;
        if (!apiKey) throw new Error('Missing OPENAI_API_KEY');
        const form = new FormData();
        form.append('file', audioBlob, 'note-recording.webm');
        form.append('model', 'whisper-1');
        const res = await fetch('https://api.openai.com/v1/audio/transcriptions', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${apiKey}` },
          body: form
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Transcription failed (${res.status}): ${t || res.statusText}`);
        }
        const data = await res.json();
        text = (data && (data.text || data.text === '' ? data.text : data.result)) || '';
      }
      if (typeof text === 'string') {
        setLiveNoteText(prev => (prev ? (prev + (prev.endsWith('\n') ? '' : '\n') + text) : text));
      }
      setNoteRecState('idle');
    } catch (e) {
      console.error('Note transcription error:', e);
      setNoteError(e?.message || 'Transcription error');
      setNoteRecState('error');
      setTimeout(() => setNoteRecState('idle'), 2500);
    }
  }

  async function startNoteRecording() {
    if (noteRecStateRef.current === 'recording' || noteRecStateRef.current === 'transcribing') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      noteStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : undefined;
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      noteMediaRecorderRef.current = mr;
      noteAudioChunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) noteAudioChunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        try {
          const blob = new Blob(noteAudioChunksRef.current, { type: mimeType || 'audio/webm' });
          if (noteStreamRef.current) noteStreamRef.current.getTracks().forEach(t => t.stop());
          await transcribeNoteAudio(blob);
        } finally {
          noteAudioChunksRef.current = [];
          if (noteIntervalRef.current) {
            try {
              const newStream = await navigator.mediaDevices.getUserMedia({ audio: true });
              noteStreamRef.current = newStream;
              const newMr = new MediaRecorder(newStream, mimeType ? { mimeType } : undefined);
              noteMediaRecorderRef.current = newMr;
              noteAudioChunksRef.current = [];
              newMr.ondataavailable = mr.ondataavailable;
              newMr.onstop = mr.onstop;
              newMr.start();
            } catch (e) { console.error('Failed to restart note recording chunk:', e); }
          }
        }
      };
      mr.start();
      noteRecStateRef.current = 'recording';
      setNoteRecState('recording');
      noteIntervalRef.current = setInterval(() => {
        try {
          if (noteMediaRecorderRef.current && noteMediaRecorderRef.current.state === 'recording') {
            noteMediaRecorderRef.current.stop();
          }
        } catch {}
      }, 5000);
    } catch (e) {
      console.error('Note recording failed:', e);
      setNoteError('Microphone access denied or recording failed');
      setNoteRecState('error');
      noteRecStateRef.current = 'error';
      setTimeout(() => { setNoteRecState('idle'); noteRecStateRef.current = 'idle'; }, 2500);
    }
  }

  function stopNoteRecording() {
    if (noteIntervalRef.current) { clearInterval(noteIntervalRef.current); noteIntervalRef.current = null; }
    noteRecStateRef.current = 'idle';
    try {
      if (noteMediaRecorderRef.current && noteMediaRecorderRef.current.state === 'recording') {
        noteMediaRecorderRef.current.stop();
      }
    } catch {}
  }

  return (
    <div className="tc-root">
      <header className="tc-topbar">
        <div className="tc-brand">Take Care Agency</div>
        <div className="tc-tabs">
          <button className={`tc-tab${view === 'patients' ? ' active' : ''}`} onClick={() => setView('patients')}>Patients</button>
          <button className={`tc-tab${view === 'staff' ? ' active' : ''}`} onClick={() => setView('staff')}>Staff</button>
        </div>
        <div className="tc-actions">
          <button
            aria-label="Toggle theme"
            className="tc-theme-toggle"
            onClick={toggleTheme}
            title={`Theme: ${theme.charAt(0).toUpperCase() + theme.slice(1)}`}
          >
            {theme === "light" && <span role="img" aria-label="Light">🌞</span>}
            {theme === "dark" && <span role="img" aria-label="Dark">🌜</span>}
            {theme === "device" && <span role="img" aria-label="Device">💻</span>}
          </button>
        </div>
      </header>

      <div className={`tc-layout${view === 'staff' ? ' staff' : ''}`}>
        {view === 'patients' && (
        <aside className="tc-sidebar">
          <div className="tc-pane-title">Patient Queue</div>
          <div className="tc-search">
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name or ID" />
          </div>
          <div className="tc-chip-row">
            <button className={`tc-chip${filter === 'inpatient' ? ' active' : ''}`} onClick={() => setFilter('inpatient')}>Inpatient</button>
            <button className={`tc-chip${filter === 'outpatient' ? ' active' : ''}`} onClick={() => setFilter('outpatient')}>Outpatient</button>
            <button className={`tc-chip${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>All</button>
          </div>
          <div className="tc-btn-row">
            <button className="tc-primary-btn" onClick={() => { setEditingPatient(null); setPatientForm({ name: '', age: '', gender: 'Male', contact: '', type: 'Inpatient', staff: (staffList[0] ? staffList[0].name : '') }); setShowPatientModal(true); }}>+ Add Patients</button>
          </div>
          <div className="tc-patient-list">
            {filtered.map(p => (
              <div
                key={p.id}
                className={`tc-patient-item${selectedPatientId === p.id ? ' active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedPatientId(p.id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedPatientId(p.id); }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src={p.avatar || DEFAULT_AVATAR} alt="avatar" className="tc-avatar" />
                  <div className="tc-patient-meta">
                    <div className="tc-patient-name">{p.name}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`tc-badge ${p.status.toLowerCase()}`}>{p.status}</span>
                  <button
                    className="tc-icon-btn danger"
                    title="Delete patient"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete patient ${p.name}? This cannot be undone.`)) return;
                      try {
                        const res = await dashboardApi.deletePatient(p.id);
                        // follow existing project convention: check status_code
                        if (res?.status_code === 200) {
                          setPatients(prev => prev.filter(x => x.id !== p.id));
                          // update selection if needed
                          if (selectedPatientId === p.id) {
                            setSelectedPatientId(prevId => {
                              const remaining = patients.filter(x => x.id !== p.id);
                              return remaining.length ? remaining[0].id : null;
                            });
                          }
                        } else {
                          alert('Failed to delete patient');
                        }
                      } catch (err) {
                        console.error('Delete patient failed', err);
                        alert('Failed to delete patient');
                      }
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
           </div>
        </aside>
        )}

        <main className="tc-center">
          {view === 'patients' && selectedPatient && (
            <>
              <section className="tc-card tc-patient-overview">
                <img src={selectedPatient.avatar || DEFAULT_AVATAR} alt="" className="tc-overview-avatar" />
                <div className="tc-overview-main">
                  <div className="tc-overview-title">
                    <div>
                      <div className="tc-name">{selectedPatient.name}</div>
                    </div>
                    <div className="tc-overview-right">
                      <div className="tc-last-session">Last session: {selectedPatient.last_session}</div>
                      {typeof selectedPatient.last_session_duration === 'number' && (
                        <div className="tc-last-session">Duration: {hhmmss(selectedPatient.last_session_duration)}</div>
                      )}
                    </div>
                  </div>
                  <div className="tc-eve-grid">
                    <ProgressBar label="Calm" value={selectedPatient.eve.calm} color="#6fcf97" />
                    <ProgressBar label="Stressed" value={selectedPatient.eve.stressed} color="#eb5757" />
                    <ProgressBar label="Demotivated" value={selectedPatient.eve.demotivated} color="#f2c94c" />
                    <ProgressBar label="Anxious" value={selectedPatient.eve.anxious} color="#bb6bd9" />
                    <ProgressBar label="Hurt" value={selectedPatient.eve.hurt} color="#56ccf2" />
                  </div>
                  {sessionHistory.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <button className="tc-link-btn" onClick={() => setShowHistory(!showHistory)}>
                        {showHistory ? 'Hide' : 'Show'} Session History ({sessionHistory.length})
                      </button>
                      {showHistory && (
                        <div style={{ marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                          {sessionHistory.map((s, i) => (
                            <div key={s.id || i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-1)', fontSize: '0.85rem' }}>
                              <div style={{ fontWeight: 700 }}>{s.date} {s.duration ? `(${s.duration})` : ''}</div>
                              {s.emotions && (
                                <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                  {Object.entries(s.emotions).filter(([k]) => ['calm','stressed','demotivated','anxious','hurt'].includes(k)).map(([k, v]) => (
                                    <span key={k} style={{ fontWeight: 600 }}>{k}: {Math.round(v)}%</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="tc-subcats">
                    {(selectedPatient.top_subcategories || []).map(s => (
                      <span key={s} className="tc-tag">{s}</span>
                    ))}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <button className="tc-secondary-btn" onClick={() => setShowPatientDetails(true)}>Patient Details</button>
                  </div>
                </div>
              </section>

              <section className="tc-row">
                <div className="tc-card tc-burnout">
                  <div className="tc-section-title">Burnout Score</div>
                  <div className="tc-burnout-score">{selectedPatient.burnout_score}%</div>
                  <div className="tc-burnout-metrics">
                    <div>Δ Mental State <b>{selectedPatient.mental_state_delta > 0 ? '+' : ''}{selectedPatient.mental_state_delta}%</b></div>
                    <div>Missed Sessions <b>{selectedPatient.missed_sessions}%</b></div>
                    <div>Language Fatigue <b>{selectedPatient.language_fatigue}%</b></div>
                  </div>
                  {showEscalateButton && (
                    <button className="tc-escalate-btn" onClick={() => setShowEscalate(true)}>Escalate</button>
                  )}
                </div>
                <div className="tc-card tc-predictions">
                  <div className="tc-section-title">Predictions</div>
                  <div className="tc-pred-charts">
                    <div>
                      <div className="tc-chart-label">Weekly</div>
                      <TinyLine points={selectedPatient.prediction?.weekly || []} color="#4e73df" />
                    </div>
                    <div>
                      <div className="tc-chart-label">Monthly</div>
                      <TinyLine points={selectedPatient.prediction?.monthly || []} color="#2ecc71" />
                    </div>
                  </div>
                  {(Array.isArray(selectedPatient.prediction?.weekly) && selectedPatient.prediction.weekly.length > 0) ||
                   (Array.isArray(selectedPatient.prediction?.monthly) && selectedPatient.prediction.monthly.length > 0) ? (
                    <div className="tc-pred-text">Risk of increasing anxiety in the next week</div>
                  ) : null}
                </div>
              </section>
            </>
          )}

          {view === 'patients' && (
          <section className="tc-row">
            <div className="tc-card tc-overview">
              <div className="tc-section-title">Agency Overview</div>
              <div className="tc-kpis">
                <div className="tc-kpi"><div className="tc-kpi-num">{patients.length}</div><div className="tc-kpi-label">Total Patients</div></div>
                <div className="tc-kpi"><div className="tc-kpi-num">{staffList.length}</div><div className="tc-kpi-label">Total Staff</div></div>
                <div className="tc-kpi"><div className="tc-kpi-num">{patients.length > 0 && staffList.length > 0 ? (staffList.length / patients.length).toFixed(1) : '0'}</div><div className="tc-kpi-label">Avg. Staff / Patient</div></div>
              </div>
            </div>
            <div className="tc-card tc-reporting">
              <div className="tc-section-title">Reporting</div>
              <div className="tc-kpis">
                <div className="tc-kpi"><div className="tc-kpi-num">{patients.length}</div><div className="tc-kpi-label">Total Patients</div></div>
                <div className="tc-kpi"><div className="tc-kpi-num">{staffList.length}</div><div className="tc-kpi-label">Total Staff</div></div>
                <div className="tc-kpi"><div className="tc-kpi-num">{patients.filter(p => p.burnout_score > 70).length}</div><div className="tc-kpi-label">Alerts Raised</div></div>
              </div>
            </div>
          </section>
          )}

          {view === 'patients' && (
          <section className="tc-card tc-alerts">
            <div className="tc-section-title">Alerts Center</div>
            <ul className="tc-alert-list">
              {patients.filter(p => p.burnout_score > 70).map(p => (
                <li key={p.id}><span className="tc-dot red" /> High risk: {p.name} (#{p.id}) — burnout {p.burnout_score}%</li>
              ))}
              {patients.filter(p => p.burnout_score > 70).length === 0 && (
                <li><span className="tc-dot green" /> No high risk patients today</li>
              )}
            </ul>
          </section>
          )}

          {view === 'staff' && (
            <section className="tc-card tc-staff">
              <div className="tc-staff-header">
                <div className="tc-section-title">Staff</div>
                <div className="tc-staff-actions">
                  <input className="tc-staff-search" value={staffQuery} onChange={e => setStaffQuery(e.target.value)} placeholder="Search by name or role" />
                  <button className="tc-primary-btn" onClick={() => { setEditingStaff(null); setStaffForm({ name: '', role: '', contact: '', patients: '' }); setShowAddStaff(true); }}>+ Add Staff</button>
                </div>
              </div>
              <div className="tc-table">
                <div className="tc-thead">
                  <div>Name</div>
                  <div>Role</div>
                  <div>Contact</div>
                  <div>Assigned Patients</div>
                  <div>Actions / Notes</div>
                </div>
                <div className="tc-tbody">
                  {staffList.filter(s => {
                    const q = staffQuery.trim().toLowerCase();
                    if (!q) return true;
                    return s.name.toLowerCase().includes(q) || s.role.toLowerCase().includes(q);
                  }).map(s => (
                    <div className="tc-tr" key={s.id}>
                      <div>{s.name}</div>
                      <div>{s.role}</div>
                      <div>{s.contact}</div>
                      <div>{(Array.isArray(s.assignedPatients) && s.assignedPatients.length > 0) ? s.assignedPatients.join(', ') : 'Not Assigned'}</div>
                      <div className="tc-row-actions">
                        <button className="tc-link-btn" onClick={() => { setEditingStaff(s); setStaffForm({ name: s.name, role: s.role, contact: s.contact }); setShowAddStaff(true); }}>Edit</button>
                        <button className="tc-link-btn danger" onClick={async () => {
                          if (!window.confirm('Are you sure you want to delete this staff member?')) return;
                          try {
                            const res = await dashboardApi.deleteStaff(s.id);
                            if (res?.status_code !== 200) { alert('Failed to delete staff'); return; }
                            setStaffList(prev => prev.filter(x => x.id !== s.id));
                          } catch (e) {
                            console.error('Delete staff failed', e);
                            alert('Failed to delete staff');
                          }
                        }}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        {view === 'patients' && (
        <aside className="tc-right">
          <div className="tc-card tc-session">
            <div className="tc-section-title">Session</div>
            <div className="tc-timer-actions">
              <button className="tc-primary-btn" onClick={() => setRunning(true)}>Start</button>
              <button className="tc-secondary-btn" onClick={() => setRunning(false)}>Pause</button>
              <button className="tc-secondary-btn" onClick={() => { setTimer(0); setRunning(false); setMicClicks(0); setSegmentResults([]); setSessionComplete(false); }}>Reset</button>
            </div>
            <div className="tc-stt" style={{ marginTop: 10 }}>
              <RecordingControls
                timer={timer}
                running={running}
                segmentRecording={segmentRecording}
                micClicks={micClicks}
                onMicClick={handleMicClick}
                selectedEntity={true}
                className="tc-mic-btn"
              />
              <div className="tc-stt-caption">Click up to 5 times. Each records 8s and aggregates results.</div>
              {sessionComplete && (
                <button className="tc-primary-btn" style={{ marginTop: 8 }} onClick={() => {
                  setSessionComplete(false);
                  setMicClicks(0);
                  setSegmentResults([]);
                  setTimer(0);
                  setRunning(false);
                }}>New Session</button>
              )}
              {lastDataSource && (
                <div className="tc-stt-caption" style={{ opacity: 0.7 }}>Data source: {lastDataSource === 'api' ? 'API' : lastDataSource === 'error' ? 'Error' : lastDataSource}</div>
              )}
              {speechWarning && (
                <div className="tc-stt-caption" style={{ color: '#e74c3c', fontWeight: 600 }}>{speechWarning}</div>
              )}
            </div>
          </div>

          <div className="tc-card tc-notes">
            <div className="tc-section-title">Session Notes</div>
            <textarea className="tc-notes-textarea" placeholder="Live transcription..." value={liveNoteText} onChange={e => setLiveNoteText(e.target.value)} />
            {noteRecState !== 'idle' && (
              <div className="tc-stt-caption" style={{ marginTop: 6 }}>
                {noteRecState === 'recording' && 'Recording… Transcribing live every 5s.'}
                {noteRecState === 'transcribing' && 'Transcribing…'}
                {noteRecState === 'error' && (noteError || 'Error')}
              </div>
            )}
            <div className="tc-notes-actions">
              {noteRecState !== 'recording' ? (
                <button className="tc-secondary-btn" onClick={startNoteRecording}>🎤 Start</button>
              ) : (
                <button className="tc-secondary-btn" onClick={stopNoteRecording}>⏹ Stop</button>
              )}
              <button className="tc-secondary-btn" onClick={() => setLiveNoteText('')}>Clear</button>
              <button className="tc-primary-btn" onClick={async () => {
                const p = selectedPatient;
                if (!p) return;
                const text = (liveNoteText || '').trim();
                if (!text) { alert('Nothing to save'); return; }
                try {
                  if (currentSessionId) {
                    const assignedStaff = staffList.find(s => s.name === p.assigned_staff);
                    if (!assignedStaff || !assignedStaff.id) { throw new Error('No assigned staff for note'); }
                    await dashboardApi.createSessionNote({ session_id: String(currentSessionId), staff_id: String(assignedStaff.id), note_text: text });
                  }
                  const today = new Date().toISOString().slice(0,10);
                  setNotesByPatient(prev => ({ ...prev, [p.id]: [...(prev[p.id] || []), `${today}: ${text}`] }));
                  setLiveNoteText('');
                } catch (error) {
                  console.error('Failed to save note:', error);
                  alert('Failed to save note. Please try again.');
                }
              }}>Save Note</button>
            </div>
            <div className="tc-notes-list" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {(notesByPatient[selectedPatientId] || []).map((n, idx) => (
                <div key={idx} className="tc-note-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span>{n}</span>
                  <button className="tc-icon-btn" aria-label="Delete note" title="Delete note" onClick={() => {
                    const pid = selectedPatientId;
                    if (!pid) return;
                    setDeletedNotes(prev => new Set([...prev, n]));
                    setNotesByPatient(prev => {
                      const list = (prev[pid] || []).slice();
                      list.splice(idx, 1);
                      return { ...prev, [pid]: list };
                    });
                  }}>🗑️</button>
                </div>
              ))}
              {!(notesByPatient[selectedPatientId] || []).length && (
                <div className="tc-note-empty">No notes yet.</div>
              )}
            </div>
          </div>
        </aside>
        )}
      </div>

      {showAddStaff && (
        <div className="tc-modal-bg" onClick={e => { if (e.target.classList.contains('tc-modal-bg')) setShowAddStaff(false); }}>
          <div className="tc-modal">
            <div className="tc-modal-title">Add Staff</div>
            <div className="tc-form">
              <label>Name<input type="text" placeholder="Full name" value={staffForm.name} onChange={e => setStaffForm(f => ({ ...f, name: e.target.value }))} /></label>
              <label>Role<input type="text" placeholder="Role" value={staffForm.role} onChange={e => setStaffForm(f => ({ ...f, role: e.target.value }))} /></label>
              <label>Contact<input type="text" placeholder="Email / Phone" value={staffForm.contact} onChange={e => setStaffForm(f => ({ ...f, contact: e.target.value }))} /></label>
            </div>
            <div className="tc-modal-actions">
              <button className="tc-secondary-btn" onClick={() => setShowAddStaff(false)}>Cancel</button>
              <button className="tc-primary-btn" onClick={async () => {
                const name = staffForm.name.trim();
                const role = staffForm.role.trim();
                const contact = staffForm.contact.trim();
                if (!name || !role || !contact) { alert('Please fill in Name, Role, and Contact'); return; }
                try {
                  if (editingStaff) {
                    const res = await dashboardApi.updateStaff(editingStaff.id, { name, designation: role, contact });
                    if (res?.status_code !== 200) { alert('Failed to update staff'); return; }
                    try {
                      const refreshed = await dashboardApi.getAllStaff();
                      if (refreshed?.status_code === 200) {
                        const staffData = (refreshed.data || []).map(s => ({ id: s.staff_id, name: s.name, role: s.designation, contact: s.contact, assignedPatients: [] }));
                        setStaffList(staffData);
                      } else {
                        setStaffList(prev => prev.map(s => s.id === editingStaff.id ? { ...s, name, role, contact } : s));
                      }
                    } catch {
                      setStaffList(prev => prev.map(s => s.id === editingStaff.id ? { ...s, name, role, contact } : s));
                    }
                  } else {
                    const response = await dashboardApi.createStaff({ name, designation: role, contact });
                    if (response.status_code === 200) {
                      const newStaff = { id: response.data.staff_id, name, role, contact, assignedPatients: [] };
                      setStaffList(prev => [newStaff, ...prev]);
                    } else { alert('Failed to create staff member'); return; }
                  }
                  setShowAddStaff(false);
                  setEditingStaff(null);
                  setStaffForm({ name: '', role: '', contact: '' });
                } catch (error) {
                  console.error('Error saving staff:', error);
                  alert('Failed to save staff member. Please try again.');
                }
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showPatientModal && (
        <div className="tc-modal-bg" onClick={e => { if (e.target.classList.contains('tc-modal-bg')) setShowPatientModal(false); }}>
          <div className="tc-modal">
            <div className="tc-modal-title">{editingPatient ? 'Edit Patient' : 'Add Patient'}</div>
            <div className="tc-form">
              <label>Name<input type="text" value={patientForm.name} onChange={e => setPatientForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" /></label>
              <label>Age<input type="number" value={patientForm.age} onChange={e => setPatientForm(f => ({ ...f, age: e.target.value }))} placeholder="Age" /></label>
              <label>Gender
                <select value={patientForm.gender} onChange={e => setPatientForm(f => ({ ...f, gender: e.target.value }))}>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </label>
              <label>Contact<input type="text" value={patientForm.contact} onChange={e => setPatientForm(f => ({ ...f, contact: e.target.value }))} placeholder="Email / Phone" /></label>
              <label>Type
                <select value={patientForm.type} onChange={e => setPatientForm(f => ({ ...f, type: e.target.value }))}>
                  <option>Inpatient</option>
                  <option>Outpatient</option>
                </select>
              </label>
              <label>Assign Staff
                <select value={patientForm.staff} onChange={e => setPatientForm(f => ({ ...f, staff: e.target.value }))}>
                  {staffList.length === 0 && (
                    <option disabled value="">No staff available. Please add staff first.</option>
                  )}
                  {staffList.map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="tc-modal-actions">
              <button className="tc-secondary-btn" onClick={() => { setShowPatientModal(false); setEditingPatient(null); }}>Cancel</button>
              <button className="tc-primary-btn" onClick={async () => {
                const name = patientForm.name.trim();
                const age = String(patientForm.age || '').trim();
                const gender = patientForm.gender;
                const contact = patientForm.contact.trim();
                const type = patientForm.type;
                const staff = patientForm.staff;
                if (!name || !age || !gender || !contact || !type) { alert('Please fill all fields'); return; }
                try {
                  if (editingPatient) {
                    const selectedStaff = staffList.find(s => s.name === staff);
                    const staffId = selectedStaff ? selectedStaff.id : null;
                    const res = await dashboardApi.updatePatient(editingPatient.id, { name, age: parseInt(age), gender, contact, type, assigned_staff_id: staffId });
                    if (res?.status_code !== 200) { alert('Failed to update patient'); return; }
                    try {
                      const patientsRes = await dashboardApi.getAllPatients();
                      if (patientsRes?.status_code === 200) {
                        const patientsData = (patientsRes.data || []).map(p => ({
                          id: p.patient_id,
                          name: p.name,
                          avatar: DEFAULT_AVATAR,
                          status: p.type,
                          last_session: new Date().toISOString().slice(0,10),
                          age: p.age?.toString() || '',
                          gender: p.gender || '',
                          contact: p.contact || '',
                          assigned_staff: p.assigned_staff_name || '',
                          eve: { calm: 0, stressed: 0, demotivated: 0, anxious: 0, hurt: 0 },
                          top_subcategories: [],
                          burnout_score: 0,
                          mental_state_delta: 0,
                          missed_sessions: 0,
                          language_fatigue: 0,
                          prediction: { weekly: [], monthly: [] }
                        }));
                        setPatients(patientsData);
                        setTimeout(() => applyLatestSessionForPatient(editingPatient.id), 100);
                      } else {
                        setPatients(prev => prev.map(p => p.id === editingPatient.id ? { ...p, name, last_session: p.last_session, status: type, assigned_staff: staff, age, gender, contact } : p));
                      }
                    } catch {
                      setPatients(prev => prev.map(p => p.id === editingPatient.id ? { ...p, name, last_session: p.last_session, status: type, assigned_staff: staff, age, gender, contact } : p));
                    }
                    setStaffList(prev => prev.map(st => {
                      const label = name;
                      let list = Array.isArray(st.assignedPatients) ? st.assignedPatients.slice() : [];
                      list = list.filter(x => x !== label);
                      if (st.name === staff) list = [...list, label];
                      return { ...st, assignedPatients: list };
                    }));
                    if (selectedPatientId === editingPatient.id) setSelectedPatientId(editingPatient.id);
                  } else {
                    const selectedStaff = staffList.find(s => s.name === staff);
                    if (!selectedStaff) { alert('Please select a valid staff member'); return; }
                    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
                    if (!uuidRegex.test(String(selectedStaff.id || ''))) { alert('Please add a staff member first (via Save in Staff). Then select that staff here.'); return; }
                    const response = await dashboardApi.createPatient({ name, age: parseInt(age), gender, contact, type, assigned_staff_id: String(selectedStaff.id) });
                    if (response.status_code === 200) {
                      const newPatient = { id: response.data.patient_id, name, avatar: DEFAULT_AVATAR, status: type, last_session: new Date().toISOString().slice(0,10), eve: { calm: 0, stressed: 0, demotivated: 0, anxious: 0, hurt: 0 }, top_subcategories: [], burnout_score: 0, mental_state_delta: 0, missed_sessions: 0, language_fatigue: 0, prediction: { weekly: [], monthly: [] }, age, gender, contact, assigned_staff: staff };
                      setPatients(prev => [newPatient, ...prev]);
                      setStaffList(prev => prev.map(st => st.name === staff ? { ...st, assignedPatients: [...(st.assignedPatients || []), name] } : st));
                      setSelectedPatientId(response.data.patient_id);
                    } else { alert('Failed to create patient'); return; }
                  }
                  setShowPatientModal(false);
                  setEditingPatient(null);
                } catch (error) {
                  console.error('Error saving patient:', error);
                  alert('Failed to save patient. Please try again.');
                }
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showPatientDetails && selectedPatient && (
        <div className="tc-modal-bg" onClick={e => { if (e.target.classList.contains('tc-modal-bg')) setShowPatientDetails(false); }}>
          <div className="tc-modal">
            <div className="tc-modal-title">Patient Details</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ position: 'relative', width: 80, height: 80 }}>
                <img 
                  src={selectedPatient.avatar || DEFAULT_AVATAR} 
                  alt="avatar" 
                  style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-1)' }} 
                />
                <label 
                  htmlFor="patient-avatar-upload"
                  style={{ 
                    position: 'absolute', bottom: 0, right: 0, 
                    background: 'var(--color-primary)', color: 'var(--primary-foreground)',
                    borderRadius: '50%', width: 24, height: 24, 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', fontSize: '0.7rem', fontWeight: 900
                  }}
                  title="Upload photo"
                >📷</label>
                <input 
                  id="patient-avatar-upload" 
                  type="file" 
                  accept="image/*" 
                  style={{ display: 'none' }} 
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !selectedPatient) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const dataUrl = ev.target.result;
                      setPatients(prev => prev.map(p => p.id === selectedPatient.id ? { ...p, avatar: dataUrl } : p));
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </div>
            </div>
            <div className="tc-details-grid">
              <div><b>Name:</b> {selectedPatient.name}</div>
              <div><b>Age:</b> {selectedPatient.age || '-'}</div>
              <div><b>Gender:</b> {selectedPatient.gender || '-'}</div>
              <div><b>Contact:</b> {selectedPatient.contact || '-'}</div>
              <div><b>Type:</b> {selectedPatient.status}</div>
              <div><b>Assigned Staff:</b> {selectedPatient.assigned_staff || '-'}</div>
            </div>
            <div className="tc-modal-actions">
              <button className="tc-secondary-btn" onClick={() => setShowPatientDetails(false)}>Close</button>
              <button className="tc-secondary-btn" onClick={async () => {
                if (!selectedPatient) return;
                if (!window.confirm(`Delete patient ${selectedPatient.name}? This cannot be undone.`)) return;
                try {
                  const res = await dashboardApi.deletePatient(selectedPatient.id);
                  if (res?.status_code !== 200) { alert('Failed to delete patient'); return; }
                  setPatients(prev => prev.filter(p => p.id !== selectedPatient.id));
                  setShowPatientDetails(false);
                  setSelectedPatientId(prevId => {
                    const remaining = patients.filter(p => p.id !== selectedPatient.id);
                    return remaining.length ? remaining[0].id : null;
                  });
                } catch (e) {
                  console.error('Delete patient failed', e);
                  alert('Failed to delete patient');
                }
              }}>Delete</button>
              <button className="tc-primary-btn" onClick={() => {
                setShowPatientDetails(false);
                setEditingPatient(selectedPatient);
                setPatientForm({ name: selectedPatient.name || '', age: selectedPatient.age || '', gender: selectedPatient.gender || 'Male', contact: selectedPatient.contact || '', type: selectedPatient.status || 'Inpatient', staff: selectedPatient.assigned_staff || (staffList[0] ? staffList[0].name : '') });
                setShowPatientModal(true);
              }}>Edit</button>
            </div>
          </div>
        </div>
      )}

      {showEscalate && (
        <div className="tc-modal-bg" onClick={e => { if (e.target.classList.contains('tc-modal-bg')) setShowEscalate(false); }}>
          <div className="tc-modal">
            <div className="tc-modal-title">Escalate Care</div>
            <div className="tc-form">
              <label>Patient<input type="text" value={selectedPatient ? `${selectedPatient.name} #${selectedPatient.id}` : ''} readOnly /></label>
              <label>Assign To<input type="text" placeholder="Specialist / Team" /></label>
              <label>Notes<textarea placeholder="Additional context" rows={3} /></label>
            </div>
            <div className="tc-modal-actions">
              <button className="tc-secondary-btn" onClick={() => setShowEscalate(false)}>Cancel</button>
              <button className="tc-primary-btn" onClick={() => setShowEscalate(false)}>Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


